import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTracker from '../../components/PageTracker/PageTracker';
import styles from './InsuranceLoans.module.css';
import { prpHttpAPI } from '../../services/prpHttpAPI';

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" fill="#3b82f6" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LoanIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="4" width="14" height="17" rx="2" fill="#fff7ed" stroke="#f97316" strokeWidth="2" />
    <path d="M9 2H15V6H9V2Z" fill="#fb923c" stroke="#f97316" strokeWidth="1.5" />
    <path d="M8 10H16M8 14H16M8 18H12" stroke="#f97316" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const ArrowRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const InsuranceLoans = () => {
  const [policies, setPolicies] = useState([]);
  const [loans, setLoans] = useState([]);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const navigate = useNavigate();



  const applyInsuranceData = (investmentData) => {
  if (investmentData?.insurance_coverage?.length > 0) {
    setPolicies(investmentData.insurance_coverage.map((p, index) => ({
      id: Date.now() + index * 2,
      ...p,
      coverage: p.coverage ? new Intl.NumberFormat('en-IN').format(Number(String(p.coverage).replace(/,/g, ''))) : '',
      premium: p.premium ? new Intl.NumberFormat('en-IN').format(Number(String(p.premium).replace(/,/g, ''))) : '',
      saved: true,
    })));
  }
  if (investmentData?.liabilities_loans?.length > 0) {
    setLoans(investmentData.liabilities_loans.map((l, index) => ({
      id: Date.now() + index * 2 + 1,
      ...l,
      total: l.total ? new Intl.NumberFormat('en-IN').format(Number(String(l.total).replace(/,/g, ''))) : '',
      outstanding: l.outstanding ? new Intl.NumberFormat('en-IN').format(Number(String(l.outstanding).replace(/,/g, ''))) : '',
      emi: l.emi ? new Intl.NumberFormat('en-IN').format(Number(String(l.emi).replace(/,/g, ''))) : '',
      saved: true,
    })));
  }
};

  React.useEffect(() => {
    const plan_id = localStorage.getItem('plan_id');
    const localtoken = localStorage.getItem('ssotoken');

    // 1. Try localStorage first (fast path)
    const savedData = localStorage.getItem('clientInvestmentData');
    let localHasInsurance = false;
    if (savedData) {
      const userData = JSON.parse(savedData);
      if ((userData.insurance_coverage?.length > 0) || (userData.liabilities_loans?.length > 0)) {
        applyInsuranceData(userData);
        localHasInsurance = true;
      }
    }

    // 2. Always fetch from API to get the latest saved data
    if (plan_id) {
      prpHttpAPI({ plan_id }, 'get-pdf/details', null, 'POST', null)
        .then((result) => {
          if (result?.success && result.investmentDetails) {
            const inv = result.investmentDetails;
            // Merge insurance/loans from API into localStorage
            const existing = savedData ? JSON.parse(savedData) : {};
            existing.insurance_coverage = inv.insurance_coverage || existing.insurance_coverage || [];
            existing.liabilities_loans = inv.liabilities_loans || existing.liabilities_loans || [];
            localStorage.setItem('clientInvestmentData', JSON.stringify({ ...inv, ...existing }));
            applyInsuranceData(inv);
          }
        })
        .catch((err) => console.error('Failed to fetch insurance data:', err));
    }
  }, []);

  const handleNext = async () => {
    const plan_id = localStorage.getItem("plan_id");
    const localtoken = localStorage.getItem("ssotoken");

    const insurance_coverage = policies.map(p => ({
      type: p.type,
      coverage: Number(String(p.coverage || '').replace(/,/g, '')) || 0,
      premium: Number(String(p.premium || '').replace(/,/g, '')) || 0,
      renewalDate: p.renewalDate,
      dependentAge: p.type === 'Term Insurance' ? (Number(p.dependentAge) || 0) : undefined,
      dependentLifeExpectancy: p.type === 'Term Insurance' ? (Number(p.dependentLifeExpectancy) || 0) : undefined
    }));

    const liabilities_loans = loans.map(l => ({
      type: l.type,
      total: Number(String(l.total || '').replace(/,/g, '')) || 0,
      outstanding: Number(String(l.outstanding || '').replace(/,/g, '')) || 0,
      remaining: Number(l.remaining) || 0,
      emi: Number(String(l.emi || '').replace(/,/g, '')) || 0
    }));

    const savedData = localStorage.getItem('clientInvestmentData');
    let userData = {};
    if (savedData) {
      userData = JSON.parse(savedData);
    }

    userData.plan_id = plan_id;
    userData.insurance_coverage = insurance_coverage;
    userData.liabilities_loans = liabilities_loans;

    localStorage.setItem('clientInvestmentData', JSON.stringify(userData));

    try {
      await prpHttpAPI(userData, 'client-investment-details', localtoken, "POST");
    } catch (error) {
      console.error("Error saving insurance and loans on next", error);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/goal-detail');
  };

  const handleSaveOnly = async () => {
    const plan_id = localStorage.getItem("plan_id");
    const localtoken = localStorage.getItem("ssotoken");

    const insurance_coverage = policies.map(p => ({
      type: p.type,
      coverage: Number(String(p.coverage || '').replace(/,/g, '')) || 0,
      premium: Number(String(p.premium || '').replace(/,/g, '')) || 0,
      renewalDate: p.renewalDate,
      dependentAge: p.type === 'Term Insurance' ? (Number(p.dependentAge) || 0) : undefined,
      dependentLifeExpectancy: p.type === 'Term Insurance' ? (Number(p.dependentLifeExpectancy) || 0) : undefined
    }));

    const liabilities_loans = loans.map(l => ({
      type: l.type,
      total: Number(String(l.total || '').replace(/,/g, '')) || 0,
      outstanding: Number(String(l.outstanding || '').replace(/,/g, '')) || 0,
      remaining: Number(l.remaining) || 0,
      emi: Number(String(l.emi || '').replace(/,/g, '')) || 0
    }));

    const savedData = localStorage.getItem('clientInvestmentData');
    let userData = {};
    if (savedData) {
      userData = JSON.parse(savedData);
    }

    userData.plan_id = plan_id;
    userData.insurance_coverage = insurance_coverage;
    userData.liabilities_loans = liabilities_loans;

    localStorage.setItem('clientInvestmentData', JSON.stringify(userData));

    try {
      await prpHttpAPI(userData, 'client-investment-details', localtoken, "POST");
      setShowSavedMessage(true);
      setTimeout(() => {
        setShowSavedMessage(false);
      }, 3000);
    } catch (error) {
      console.error("Error saving insurance and loans", error);
    }
  };

  const handleBack = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/client-investments');
  };


  // Insurance Handlers
 const addPolicy = () => {
  setPolicies([
    { id: Date.now(), type: 'Term Insurance', coverage: '', premium: '', renewalDate: '', dependentAge: '', dependentLifeExpectancy: '' , saved: false},
    ...policies
  ]);
};

  const updatePolicy = (id, field, value) => {
    setPolicies(policies.map(p => {
      if (p.id === id) {
        if (field === 'coverage' || field === 'premium') {
          const raw = value.replace(/,/g, '');
          if (raw === '') {
            return { ...p, [field]: '' };
          } else if (/^[0-9]+$/.test(raw) && Number(raw) <= 1000000000) {
            return { ...p, [field]: new Intl.NumberFormat('en-IN').format(Number(raw)) };
          }
          return p;
        }
        return { ...p, [field]: value };
      }
      return p;
    }));
  };

  const removePolicy = (id) => {
    setPolicies(policies.filter(p => p.id !== id));
  };

  // Loan Handlers
 const addLoan = () => {
  setLoans([
    { id: Date.now(), type: 'Home Loan', total: '', outstanding: '', remaining: '', emi: '' , saved: false},
    ...loans
  ]);
};
const handleEditPolicyClick = (id, state) => {
  if (state === true) {
    const p = policies.find(p => p.id === id);
    if (!p.coverage || !p.premium) {
      alert('Please fill Coverage and Premium before saving.');
      return;
    }
  }
  setPolicies(prev => prev.map(p => p.id === id ? { ...p, saved: state } : p));
};

const handleEditLoanClick = (id, state) => {
  if (state === true) {
    const l = loans.find(l => l.id === id);
    if (!l.outstanding || !l.emi) {
      alert('Please fill Outstanding Loan and EMI before saving.');
      return;
    }
  }
  setLoans(prev => prev.map(l => l.id === id ? { ...l, saved: state } : l));
};
  const updateLoan = (id, field, value) => {
    setLoans(loans.map(l => {
      if (l.id === id) {
        if (field === 'total' || field === 'outstanding' || field === 'emi') {
          const raw = value.replace(/,/g, '');
          if (raw === '') {
            return { ...l, [field]: '' };
          } else if (/^[0-9]+$/.test(raw) && Number(raw) <= 1000000000) {
            return { ...l, [field]: new Intl.NumberFormat('en-IN').format(Number(raw)) };
          }
          return l;
        }
        return { ...l, [field]: value };
      }
      return l;
    }));
  };

  const removeLoan = (id) => {
    setLoans(loans.filter(l => l.id !== id));
  };

  // Calculations
  const totals = useMemo(() => {
    const outstanding = loans.reduce((sum, l) => sum + (Number(String(l.outstanding || '').replace(/,/g, '')) || 0), 0);
    const monthlyEmi = loans.reduce((sum, l) => sum + (Number(String(l.emi || '').replace(/,/g, '')) || 0), 0);
    return { outstanding, monthlyEmi, count: loans.length };
  }, [loans]);

  return (
    <div className={styles.container}>
      <PageTracker currentStep={3} />
      <div className={styles.main}>
        {/* Insurance Section */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>
              <ShieldIcon /> Insurance Coverage
            </div>
          </div>
          <div className={styles.cardDescription}>
            Add existing policies.
          </div>

          {policies.length === 0 ? (
            <div className={styles.noData}>No policies added. Click Add Policy to begin.</div>
          ) : (
            <>
              <div className={`${styles.tableHeader} ${styles.insuranceGrid}`}>
                <span className={styles.columnLabel}>Policy Type</span>
                <span className={styles.columnLabel}>Coverage (₹)</span>
                <span className={styles.columnLabel}>Annual Premium (₹)</span>
                <span className={styles.columnLabel}>Renewal / Maturity Date</span>
                <span></span>
              </div>
             {policies.map((policy) => (
  <div key={policy.id} className={styles.rowContainer}>

    {/* Header with Saved/Edit status */}
    <div className={styles.itemHeader}>
      <div className={styles.itemTitle}>{policy.type}</div>
      <div className={styles.itemActions}>
        {policy.saved ? (
          <div className={styles.savedStatus}>
            <span className={styles.savedText}>✓ Saved</span>
            <button className={styles.editBtn} onClick={() => handleEditPolicyClick(policy.id, false)}>
              <PencilIcon /> Edit
            </button>
          </div>
        ) : (
          <>
            <span className={styles.enterAmount}>Enter details</span>
            <button className={styles.closeIconBtn} onClick={() => removePolicy(policy.id)}>
              <XIcon />
            </button>
          </>
        )}
      </div>
    </div>

    <div className={`${styles.row} ${styles.insuranceGrid}`}>
      <select
        className={`${styles.inputField} ${styles.selectField}`}
        value={policy.type}
        disabled={policy.saved}
        onChange={(e) => updatePolicy(policy.id, 'type', e.target.value)}
      >
        <option value="Term Insurance">Term Insurance</option>
        <option value="Health Insurance">Health Insurance</option>
        <option value="Car Insurance">Car Insurance</option>
        <option value="Home Insurance">Home Insurance</option>
        <option value="Other">Other</option>
      </select>
      <input
        type="text"
        placeholder="Coverage (Rs)"
        className={styles.inputField}
        value={policy.coverage}
        disabled={policy.saved}
        onChange={(e) => updatePolicy(policy.id, 'coverage', e.target.value)}
      />
      <input
        type="text"
        placeholder="Annual Premium (Rs)"
        className={styles.inputField}
        value={policy.premium}
        disabled={policy.saved}
        onChange={(e) => updatePolicy(policy.id, 'premium', e.target.value)}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
        <input
          type="date"
          className={styles.inputField}
          value={policy.renewalDate}
          disabled={policy.saved}
          onChange={(e) => updatePolicy(policy.id, 'renewalDate', e.target.value)}
          title="Enter the next renewal or maturity date, not today's date"
        />
        <span style={{ fontSize: '0.65rem', color: '#64748b', whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>
          (Enter Renewal/Maturity date)
        </span>
      </div>
      <span></span>
    </div>

    {policy.type === 'Term Insurance' && (
      <div className={styles.termDetailsBox}>
        <div className={styles.termDetailsField}>
          <label className={styles.subLabel}>Dependent Age</label>
          <input
            type="text"
            placeholder="e.g. 35"
            className={styles.subInput}
            value={policy.dependentAge || ''}
            disabled={policy.saved}
            onChange={(e) => updatePolicy(policy.id, 'dependentAge', e.target.value.replace(/[^0-9]/g, ''))}
          />
        </div>
        <div className={styles.termDetailsField}>
          <label className={styles.subLabel}>Dependent Life Expectancy</label>
          <input
            type="text"
            placeholder="e.g. 80"
            className={styles.subInput}
            value={policy.dependentLifeExpectancy || ''}
            disabled={policy.saved}
            onChange={(e) => updatePolicy(policy.id, 'dependentLifeExpectancy', e.target.value.replace(/[^0-9]/g, ''))}
          />
        </div>
      </div>
    )}

    {!policy.saved && (
      <button className={styles.saveItemBtn} onClick={() => handleEditPolicyClick(policy.id, true)}>
        ✓ Save Policy
      </button>
    )}
  </div>
))}
          </>
        )}

          <button className={styles.addBtn} onClick={addPolicy}>
            + Add Policy
          </button>


        </div>

        {/* Liabilities & Loans Section */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>
              <LoanIcon /> Liabilities & Loans
            </div>
          </div>
          <div className={styles.cardDescription}>
            Record active loans to assess net financial position. EMI obligations are referenced in the plan.
          </div>

          <div className={`${styles.tableHeader} ${styles.loanGrid}`}>
            <div className={styles.columnLabel}>LOAN TYPE</div>
            <div className={styles.columnLabel}>TOTAL LOAN (₹)</div>
            <div className={styles.columnLabel}>OUTSTANDING LOAN (₹)</div>
            <div className={styles.columnLabel}>REMAINING (YRS)</div>
            <div className={styles.columnLabel}>EMI / MO (₹)</div>
            <div></div>
          </div>

          {loans.length === 0 ? (
            <div className={styles.noData}>No loans added yet.</div>
          ) : (
            loans.map((loan) => (
  <div key={loan.id} className={styles.rowContainer}>
    <div className={styles.itemHeader}>
      <div className={styles.itemTitle}>{loan.type}</div>
      <div className={styles.itemActions}>
        {loan.saved ? (
          <div className={styles.savedStatus}>
            <span className={styles.savedText}>✓ Saved</span>
            <button className={styles.editBtn} onClick={() => handleEditLoanClick(loan.id, false)}>
              <PencilIcon /> Edit
            </button>
          </div>
        ) : (
          <>
            <span className={styles.enterAmount}>Enter details</span>
            <button className={styles.closeIconBtn} onClick={() => removeLoan(loan.id)}>
              <XIcon />
            </button>
          </>
        )}
      </div>
    </div>

    <div className={`${styles.row} ${styles.loanGrid}`}>
      <select
        className={`${styles.inputField} ${styles.selectField}`}
        value={loan.type}
        disabled={loan.saved}
        onChange={(e) => updateLoan(loan.id, 'type', e.target.value)}
      >
        <option value="Home Loan">Home Loan</option>
        <option value="Car Loan">Car Loan</option>
        <option value="Personal Loan">Personal Loan</option>
        <option value="Education Loan">Education Loan</option>
        <option value="Business Loan">Business Loan</option>
        <option value="Other">Other</option>
      </select>
      <input type="text" placeholder="Total Loan (₹)" className={styles.inputField}
        value={loan.total} disabled={loan.saved}
        onChange={(e) => updateLoan(loan.id, 'total', e.target.value)} />
      <input type="text" placeholder="Outstanding (₹)" className={styles.inputField}
        value={loan.outstanding} disabled={loan.saved}
        onChange={(e) => updateLoan(loan.id, 'outstanding', e.target.value)} />
      <input type="number" placeholder="Remaining (yrs)" className={styles.inputField}
        value={loan.remaining} disabled={loan.saved}
        onChange={(e) => updateLoan(loan.id, 'remaining', e.target.value)} />
      <input type="text" placeholder="EMI / mo (₹)" className={styles.inputField}
        value={loan.emi} disabled={loan.saved}
        onChange={(e) => updateLoan(loan.id, 'emi', e.target.value)} />
      <span></span>
    </div>

    {!loan.saved && (
      <button className={styles.saveItemBtn} onClick={() => handleEditLoanClick(loan.id, true)}>
        ✓ Save Loan
      </button>
    )}
  </div>
))
          )}

          <button className={styles.addBtn} onClick={addLoan}>
            + Add Loan
          </button>

          {loans.length > 0 && (
            <div className={styles.summaryBar}>
              Total Outstanding Loan: ₹{totals.outstanding.toLocaleString()} | Total Monthly EMI: ₹{totals.monthlyEmi.toLocaleString()}/mo ({totals.count} {totals.count === 1 ? 'loan' : 'loans'})
            </div>
          )}
        </div>

        {showSavedMessage && (
          <div className={styles.saveAlert}>
            ✓ Protection and Liabilities details saved successfully!
          </div>
        )}

        <div className={styles.footer}>
          <button className={styles.saveBtn} onClick={handleSaveOnly}>
            Save Protection
          </button>
          <div className={styles.footerRight}>
            <button className={styles.backBtn} onClick={handleBack}>
              Back
            </button>
            <button className={styles.nextBtn} onClick={handleNext}>
              Next — Goals <span className={styles.arrowIcon}>→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default InsuranceLoans;
